import type { FormEvent } from 'react';
import Select from '../../../components/form/AppSelect';
import NumberInput from '../../../components/form/NumberInput';
import { useTranslation } from 'react-i18next';
import { generateRandomColor } from '../../../shared/colorUtils';
import type { CategoryFormData, WarehouseCategory } from '../types';

interface CategoryFormModalProps {
  mode: 'add' | 'edit';
  categories: WarehouseCategory[];
  childrenByParent: Record<string, WarehouseCategory[]>;
  selectedCategory?: WarehouseCategory | null;
  formData: CategoryFormData;
  onChange: (field: keyof CategoryFormData, value: string | number | null) => void;
  onClose: () => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}

const CategoryFormModal = ({
  mode,
  categories,
  childrenByParent,
  selectedCategory,
  formData,
  onChange,
  onClose,
  onSubmit,
}: CategoryFormModalProps) => {
  const { t } = useTranslation();
  const isEdit = mode === 'edit';
  const selectedCategoryId = selectedCategory?.id;
  const hasChildren = selectedCategoryId !== undefined && (childrenByParent[String(selectedCategoryId)]?.length ?? 0) > 0;

  const descendantIds = new Set<number | string>();
  if (isEdit && selectedCategoryId !== undefined) {
    const queue = [selectedCategoryId];
    while (queue.length > 0) {
      const parentId = queue.shift();
      (childrenByParent[String(parentId)] || []).forEach((child) => {
        descendantIds.add(child.id);
        queue.push(child.id);
      });
    }
  }

  const parentOptions = categories
    .filter((category) => category.id !== selectedCategoryId && !descendantIds.has(category.id))
    .map((category) => ({ value: category.id, label: category.name }));
  const selectedParentOption = formData.parent
    ? parentOptions.find((option) => option.value === formData.parent) || null
    : null;

  return (
    <div className="modal modal-blur show" style={{ display: 'block' }} tabIndex={-1} role="dialog">
      <div className="modal-dialog modal-dialog-centered" role="document">
        <form onSubmit={onSubmit} className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">{isEdit ? t('warehouses.categories.edit') : t('warehouses.categories.add')}</h5>
            <button type="button" className="btn-close" onClick={onClose}></button>
          </div>
          <div className="modal-body">
            <div className="mb-3">
              <label className="form-label required">{t('warehouses.categories.name')}</label>
              <input
                type="text"
                className="form-control"
                name="name"
                value={formData.name}
                onChange={(event) => onChange('name', event.target.value)}
                required
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{t('warehouses.categories.parentCategory')}</label>
              {isEdit && hasChildren ? (
                <div className="form-control-plaintext text-muted" style={{ fontSize: '0.875rem' }}>
                  {t('warehouses.categories.cannotSetParentHasChildren') || 'Nelze nastavit rodiče – kategorie má podkategorie.'}
                </div>
              ) : (
                <Select
                  value={selectedParentOption}
                  onChange={(option) => onChange('parent', option ? option.value : null)}
                  options={parentOptions}
                  isClearable
                  placeholder={t('warehouses.categories.noParent')}
                  className="react-select-container"
                  classNamePrefix="react-select"
                />
              )}
            </div>
            <div className="mb-3">
              <label className="form-label">{t('warehouses.categories.priority')}</label>
              <NumberInput
                name="priority"
                step="any"
                value={formData.priority}
                onChange={(priority) => onChange('priority', priority)}
              />
            </div>
            <div className="mb-3">
              <label className="form-label">{t('warehouses.categories.description')}</label>
              <textarea
                className="form-control"
                name="description"
                value={formData.description}
                onChange={(event) => onChange('description', event.target.value)}
                rows={3}
              ></textarea>
            </div>
            <div className="mb-3">
              <label className="form-label">{t('warehouses.categories.color')}</label>
              <div className="input-group">
                <input
                  type="color"
                  className="form-control form-control-color"
                  name="color"
                  value={formData.color}
                  onChange={(event) => onChange('color', event.target.value)}
                  title={t('common.pickColor')}
                  style={{ maxWidth: 50 }}
                />
                <input
                  type="text"
                  className="form-control font-monospace"
                  name="color"
                  value={formData.color}
                  onChange={(event) => onChange('color', event.target.value)}
                  maxLength={7}
                  style={{ maxWidth: 100 }}
                />
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={() => onChange('color', generateRandomColor())}
                  title={t('common.generateColor') || 'Generate random color'}
                >
                  <i className="ti ti-color-swatch" aria-hidden="true" />
                </button>
                <span
                  className="input-group-text"
                  style={{ backgroundColor: formData.color, width: 36, border: '1px solid var(--tblr-border-color)' }}
                />
              </div>
            </div>
          </div>
          <div className="modal-footer">
            <button type="button" className="btn btn-link link-secondary" onClick={onClose}>
              {t('common.cancel')}
            </button>
            <button type="submit" className="btn btn-primary ms-auto">
              {isEdit ? t('common.save') : t('common.create')}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CategoryFormModal;
